import {useCallback, useState} from 'react'
// eslint-disable-next-line no-restricted-imports
import {reportError} from '@github-ui/failbot'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {useClickAnalytics} from '@github-ui/use-analytics'
import {FixWithCopilotError, type CopilotFixDialogSubmitOptions, type KnownBillingEntityError} from './types'

const COPILOT_SETTINGS_PATH = '/settings/copilot/features'
const CUSTOMER_SELECTION_REQUIRED_ERROR = 'customer selection required'
const CUSTOMER_SELECTION_REQUIRED_DESCRIPTION =
  'Before you can use Copilot cloud agent, you need to pick a "Usage billed to" option in your Copilot settings.'

const PREMIUM_BILLING_DOCS_URL =
  'https://docs.github.com/copilot/how-tos/manage-and-track-spending/monitor-premium-requests#managing-premium-request-billing-with-multiple-copilot-licenses'

const KNOWN_ERRORS: readonly KnownBillingEntityError[] = [
  // The user has not selected a billing entity
  {
    match: CUSTOMER_SELECTION_REQUIRED_ERROR,
    message: CUSTOMER_SELECTION_REQUIRED_DESCRIPTION,
    settingsPath: COPILOT_SETTINGS_PATH,
    docsLink: PREMIUM_BILLING_DOCS_URL,
  },
  // The user has selected a billing entity but has no premium quota remaining
  {
    match: 'insufficient premium quota',
    message: `The billing entity you have selected has no premium quota remaining.`,
    settingsPath: COPILOT_SETTINGS_PATH,
    docsLink: PREMIUM_BILLING_DOCS_URL,
  },
]

interface UseFixWithCopilotOptions {
  basePath: string
}

export interface SubmitFixOptions extends CopilotFixDialogSubmitOptions {
  reviewReferences: string[]
  fixAll: boolean
  /** Identifies a unified review-comment batch for server-side event segmentation. */
  isBatch?: boolean
  /** Additional properties merged into the analytics event. */
  analyticsExtras?: Record<string, unknown>
}

/**
 * Encapsulates the shared submission logic for the "Fix with Copilot" flow:
 * POST to `code_review_fix_with_copilot`, error handling, analytics, and
 * task URL extraction.
 */
export function useFixWithCopilot({basePath}: UseFixWithCopilotOptions) {
  const {sendClickAnalyticsEvent} = useClickAnalytics()
  const [taskUrl, setTaskUrl] = useState<string | null>(null)
  const resetTaskUrl = useCallback(() => setTaskUrl(null), [])

  const submitFix = useCallback(
    async ({
      createPullRequest,
      instructions,
      model,
      reviewReferences,
      fixAll,
      isBatch,
      analyticsExtras,
    }: SubmitFixOptions) => {
      const response = await verifiedFetchJSON(`${basePath}/code_review_fix_with_copilot`, {
        method: 'POST',
        body: {
          create_pull_request: createPullRequest,
          fix_all: fixAll,
          review_references: reviewReferences,
          ...(isBatch ? {is_batch: true} : {}),
          ...(model ? {model} : {}),
          ...(instructions ? {additional_instructions: instructions} : {}),
        },
      })

      if (!response.ok) {
        let data = null
        try {
          data = await response.json()
        } catch {
          // ignore JSON parse errors
        }
        const errorValue = data?.error || data?.message
        const serverMessage = typeof errorValue === 'string' ? errorValue : ''
        const knownError = KNOWN_ERRORS.find(e => serverMessage.toLowerCase().includes(e.match))
        if (knownError) {
          throw new FixWithCopilotError(knownError.message, knownError)
        }
        if (serverMessage) {
          reportError(new Error(`CCR FixWithCopilot error: ${serverMessage}`))
        }
        throw new Error(`Failed to Fix with Copilot. Please try again. (${response.status})`)
      }

      const data = await response.json()

      // Validate the task URL before emitting success analytics so a malformed
      // HTTP-success response (no task_url) is treated as a failure rather than
      // recording a successful submission.
      if (!data?.task_url) {
        throw new Error('No task URL returned')
      }
      setTaskUrl(data.task_url)

      sendClickAnalyticsEvent({
        ...analyticsExtras,
        category: 'ccr_fix_with_copilot',
        action: 'submit',
        applyMode: createPullRequest ? 'pull_request' : 'commit',
      })
    },
    [basePath, sendClickAnalyticsEvent],
  )

  return {taskUrl, submitFix, resetTaskUrl}
}
